"use client"

import { useState } from "react"
import { Button } from "@/shared/components/ui/button"
import { Card, CardContent } from "@/shared/components/ui/card"
import { Badge } from "@/shared/components/ui/badge"
import { Navigation, BadgeCheck, MoonStar, Sun } from "lucide-react"
import type { TravelPackage } from "@/shared/types/packages"
import { PackageDetailsModal } from "./SharedDepartureDetails"
import { Link } from "react-router-dom"

// Helper function to get category-specific badge styles
const getCategoryBadgeStyle = (category: string) => {
  const lowerCategory = category.toLowerCase()
  
  switch (lowerCategory) {
    case 'cruise':
      return 'bg-amber-600 hover:bg-rust-700 text-white shadow-md border-0'
    case 'international':
      return 'bg-orange-500 hover:bg-orange-600 text-white shadow-md border-0'
    case 'domestic':
      return 'bg-orange-400 hover:bg-orange-500 text-white shadow-md border-0'
    default:
      return 'bg-gray-800 hover:bg-gray-900 text-white shadow-md border-0'
  }
}

const getPrimaryCountry = (location: string) => {
  if (!location) return ''
  const firstSegment = location.split(/\s*(?:,|&|and|\/)\s*/)[0]?.trim()
  return firstSegment || location
}

interface PackageCardProps {
  package: TravelPackage
}

export function PackageCard({ package: pkg }: PackageCardProps) {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [showAllHighlights, setShowAllHighlights] = useState(false)
  const [imageError, setImageError] = useState(false)
  
  // Debug: Log image URL for this package
  const imageUrl = imageError ? "/logo.svg" : (pkg.image || "/logo.svg")
  // Development logging disabled for production
  // if (pkg.image && pkg.image !== "/logo.svg") {
  //   console.log(`PackageCard for "${pkg.title}" using image:`, imageUrl)
  // }

  const handleShowAllHighlights = () => {
    setShowAllHighlights(true)
  }

  return (
    <>
      <Card className="group overflow-hidden card-interactive bg-white border-0 shadow-lg h-full flex flex-col gpu-accelerated hover-glow">
        <div className="relative overflow-hidden">
          <img
            src={imageUrl}
            alt={pkg.title}
            loading="lazy"
            style={{ aspectRatio: '4/3' }}
            onError={() => {
              // console.log(`Image load error for "${pkg.title}":`, pkg.image)
              setImageError(true)
            }}
            onLoad={() => {
              // Development logging disabled
              // if (pkg.image && pkg.image !== "/logo.svg") {
              //   console.log(`Image loaded successfully for "${pkg.title}":`, pkg.image)
              // }
            }}
            className="w-full h-auto object-cover transition-transform duration-300 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-smooth"></div>
          <div className="absolute top-4 left-4 flex gap-2">
            <Badge className={getCategoryBadgeStyle(pkg.category)}>{pkg.category}</Badge>
          </div>
        </div>

        <CardContent className="p-6 flex-1 flex flex-col">
          <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-orange-600 transition-colors">{pkg.title}</h3>
          <p className="text-sm text-gray-600 mb-4 line-clamp-2 leading-relaxed">{pkg.overview}</p>

          <div className="flex items-center justify-between mb-4 text-sm text-gray-600">
            <div className="flex items-center space-x-3">
              <div className="flex items-center space-x-1">
                <Sun className="h-4 w-4 text-yellow-500" />
                <span>{pkg.days} days</span>
              </div>
              <div className="flex items-center space-x-1">
                <MoonStar className="h-4 w-4 text-blue-500" />
                <span>{pkg.nights} nights</span>
              </div>
            </div>
            <div className="flex items-center space-x-1 min-w-0">
              <Navigation className="h-4 w-4" />
              <span className="text-sm text-gray-600 truncate min-w-0">{getPrimaryCountry(pkg.location)}</span>
            </div>
          </div>

          <div className="mb-4">
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Package Highlights</h4>
            <div className="flex flex-wrap gap-1.5">
              {pkg.packageHighlights && pkg.packageHighlights.length > 0 ? (
                <>
                  {pkg.packageHighlights.slice(0, 2).map((highlight, index) => (
                    <Badge key={index} variant="secondary" className="text-xs bg-blue-50 text-blue-700 hover:bg-blue-100 py-1.5 px-2.5">
                      {highlight}
                    </Badge>
                  ))}
                  {pkg.packageHighlights.length > 2 && !showAllHighlights && (
                    <Badge
                      variant="secondary"
                      className="text-xs bg-gray-100 text-gray-600 cursor-pointer hover:bg-gray-200 py-1.5 px-2.5 min-h-[32px] min-w-[44px] flex items-center justify-center touch-manipulation"
                      onClick={handleShowAllHighlights}
                    >
                      +{pkg.packageHighlights.length - 2} more
                    </Badge>
                  )}
                  {showAllHighlights && pkg.packageHighlights.length > 2 && (
                    <>
                      {pkg.packageHighlights.slice(2).map((highlight, index) => (
                        <Badge
                          key={index + 2}
                          variant="secondary"
                          className="text-xs bg-blue-50 text-blue-700 hover:bg-blue-100 py-1.5 px-2.5"
                        >
                          {highlight}
                        </Badge>
                      ))}
                    </>
                  )}
                </>
              ) : (
                <span className="text-xs text-gray-500 italic">No highlights available</span>
              )}
            </div>
          </div>

          <div className="flex items-center space-x-2 mb-4">
            {pkg.category.toLowerCase() !== 'domestic' && (
              pkg.visaRequired ? (
                <div className="flex items-center space-x-1">
                  <div className="h-4 w-4 rounded-full bg-yellow-500" />
                  <span className="text-sm text-yellow-600 font-medium">Visa Required</span>
                </div>
              ) : (
                <>
                  <BadgeCheck className="h-4 w-4 text-green-500" />
                  <span className="text-sm text-green-600 font-medium">Visa Free</span>
                </>
              )
            )}
          </div>

          <div className="space-y-2 mt-auto pt-4">
            <Button
              variant="outline"
              className="w-full border-amber-500 text-amber-600 hover:bg-amber-50 bg-transparent transition-all hover:border-amber-600 hover:scale-[1.02]"
              asChild
            >
              <Link to="/contact">Message Us</Link>
            </Button>
            <Button
              variant="ghost"
              className="w-full btn-autumn-primary transition-all hover:scale-[1.02] shadow-md hover:shadow-lg"
              style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #ea580c 100%)', color: 'white' }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgb(234 88 12)'
                e.currentTarget.style.color = 'white'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'rgb(249 115 22)'
                e.currentTarget.style.color = 'white'
              }}
              onClick={() => setIsModalOpen(true)}
            >
              View Package Details →
            </Button>
          </div>
        </CardContent>
      </Card>

      <PackageDetailsModal package={pkg} isOpen={isModalOpen} onOpenChange={setIsModalOpen} />
    </>
  )
}
